import { DocumentMagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React from "react";
import { Link } from "react-router-dom";

import { Button, Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { useManuallyMarkMediaAsViewedMutation } from "@api/graphql/types-and-hooks";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { MediaCardType } from "@pages/VideoSessionsV2/components/MediaCards";
import { SessionRating } from "@pages/VideoSessionsV2/components/Utils/SessionRating";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

import { roleMapping } from "../../../layouts/config";

interface HeaderContentProps {
  learnerId: string;
  learnerFullName: string;
  providerFirstName?: string;
  providerLastName?: string;
  date: string;
  sessionRating?: number;
  media?: MediaCardType;
  isNoMediaStatus?: boolean;
  userType?: "Caregiver" | "Provider";
}

export const HeaderContent: React.FC<HeaderContentProps> = ({
  learnerId,
  learnerFullName,
  providerFirstName,
  providerLastName,
  date,
  sessionRating,
  media,
  isNoMediaStatus,
  userType = "Provider",
}) => {
  const { roles } = useAdminData();
  const canAccessClientDetails = roleMapping["/clients"].some((r) => roles.includes(r));
  const { setSelectedTab, isVideoDetailsPage, allFilteredMedias, setAllFilteredMedias } = useVideoSessionData();
  const markAsReviewedMutation = useManuallyMarkMediaAsViewedMutation();
  const { isPending } = markAsReviewedMutation;
  const { isMobile } = useBreakpoint();

  const handleMarkAsReviewed = async (isReviewed: boolean) => {
    const response = await markAsReviewedMutation.mutateAsync({
      mediId: media ? media?.id : "",
      isReviewed,
    });
    if (response.manuallyMarkMediaAsViewed?.status) {
      setAllFilteredMedias([
        ...allFilteredMedias.map((listMedia) =>
          listMedia.id === response.manuallyMarkMediaAsViewed?.media?.id
            ? { ...listMedia, isViewed: response.manuallyMarkMediaAsViewed.media.isViewed }
            : listMedia,
        ),
      ]);
    }
  };

  const userRating = (
    <div className="flex flex-wrap gap-2 lg:mt-1">
      <Paragraph colorType="secondary" className={`text-sm ${isMobile && "hidden"}`}>
        Attending {userType}:{" "}
      </Paragraph>
      <UserInitialsView firstName={providerFirstName || ""} lastName={providerLastName || ""} />
      {sessionRating && <SessionRating rating={sessionRating} shortView={isMobile} />}
    </div>
  );

  const reviewedButtonText = media?.isViewed ? (
    <Small className="text-text-primary">
      Reviewed <CheckIcon className="h-4 w-5 text-interaction-primary inline stroke-5 drop-shadow-md" />
    </Small>
  ) : (
    "Mark As Reviewed"
  );

  const markReviewedClass = clsx(
    "rounded-lg px-3! py-2! text-text-primary! text-sm! font-normal! !hover:bg-limestone-50",
    media?.isViewed ? "ring-gray-200! bg-gray-50!" : "ring-limestone-200!",
  );

  return (
    <div className="flex justify-between w-[calc(100%-56px)]">
      <div className="w-full">
        <div className="flex flex-col lg:flex-row sm:items-baseline gap-1 sm:gap-4 w-full">
          <Link
            className={clsx(canAccessClientDetails && "hover:underline")}
            to={canAccessClientDetails ? `/clients/${learnerId}/programming` : ""}
          >
            <Headline
              size="medium"
              className={`ph-no-capture ${isMobile && "whitespace-nowrap w-full text-ellipsis overflow-hidden text-xl"}`}
            >
              {learnerFullName}
            </Headline>
          </Link>
          <div className={`${isMobile && "flex justify-between items-center w-full"}`}>
            <Paragraph className={`${isMobile && "text-xs!"}`} displayType="normal">
              {date}
            </Paragraph>
            {isMobile && userRating}
          </div>
        </div>
        {!isMobile && userRating}
      </div>
      {isVideoDetailsPage ? null : (
        <div className="flex flex-row gap-2 items-center">
          <div className="w-1/10">
            <DocumentMagnifyingGlassIcon
              onClick={(e) => {
                e.preventDefault();
                setSelectedTab("transcript");
              }}
              className={clsx(
                "h-6 w-6 text-limestone-600 inline mr-2",
                isNoMediaStatus && "pointer-events-none opacity-10",
              )}
            />
          </div>
          {roles.includes("bcba") ? (
            <Button
              text={reviewedButtonText}
              disabled={isPending || isNoMediaStatus}
              appearance="secondary"
              onClick={(e) => {
                e?.stopPropagation();
                e?.preventDefault();
                if (media) {
                  handleMarkAsReviewed(!media.isViewed);
                }
                return;
              }}
              className={markReviewedClass}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};
